import { Control, FieldPath, FieldValues } from "react-hook-form";
import {
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@root/server/client/esweb/components/ui/form.tsx";
import { Textarea } from "@root/server/client/esweb/components/ui/textarea.tsx";
import { useEffect, useRef } from "react";

interface FormTextareaProps<T extends FieldValues> extends React.ComponentProps<"textarea"> {
    control: Control<T> | Control<any>;
    name: FieldPath<T>;
    label?: string;
    description?: string;
    autoGrow?: boolean;
}

export const FormTextarea = <T extends FieldValues>({
                                                        control,
                                                        name,
                                                        label,
                                                        description,
                                                        autoGrow = true,
                                                        ...props
                                                    }: FormTextareaProps<T>) => {
    
    // Internal ref to access the DOM element for resizing
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const adjustHeight = () => {
        const el = textareaRef.current;
        if (el && autoGrow) {
            el.style.height = "auto";
            el.style.height = `${el.scrollHeight}px`;
        }
    };

    return (
        <FormField
            control={control as Control<T>}
            name={name}
            render={({ field }) => {
                
                // Adjust height whenever value changes
                useEffect(() => {
                    adjustHeight();
                }, [field.value, autoGrow]);

                return (
                    <FormItem>
                        {label && <FormLabel>{label}</FormLabel>}
                        <FormControl>
                            <Textarea
                                {...props}
                                {...field}
                                ref={(e) => {
                                    // Maintain React Hook Form's ref
                                    field.ref(e);
                                    // @ts-ignore
                                    textareaRef.current = e;
                                }}
                                onChange={(e) => {
                                    field.onChange(e);
                                    adjustHeight();
                                }}
                                className={autoGrow ? "resize-none overflow-hidden min-h-[80px]" : ""}
                            />
                        </FormControl>
                        {description && <FormDescription>{description}</FormDescription>}
                        <FormMessage />
                    </FormItem>
                )
            }}
        />
    );
};